import React, { useMemo, useContext, useEffect } from 'react';

import {
  Section,
  Grid,
  normalizeMultiSelectValue,
  MultiSelect,
} from '@gogame/components';
import {
  FormField,
  FormContext,
  getIn,
  omitFormProps,
  withFormField,
  useChangeMultipleValues,
} from '@gogame/form';

import { DEPENDENCIES } from 'scenes/CustomAudiences/Audiences/components/AudienceForm/constants';
import {
  loadAffiliates,
  loadExcludeApplications,
  loadApplications,
  loadCountries,
  loadNetworks,
  loadPlatforms,
  loadTrafficTypes,
  loadOSversions,
  loadDevices,
} from 'scenes/CustomAudiences/Audiences/sources/fields';
import { useCanEditAll } from 'scenes/CustomAudiences/EditGranularityContext';

import {
  NETWORKS,
  PLATFORMS,
  APPLICATIONS,
  EXCLUDE_APPLICATIONS,
  ONLY_APPLICATIONS,
  TRAFFIC_TYPE,
  COUNTRIES,
  AFFILIATES,
  DEVICES,
  OSVERSIONS,
  hasMobileNetworks,
  TRAFFIC_TYPE_ADVERTISING,
  TRAFFIC_TYPE_ALL,
} from './constants';
import { IS_INCLUDE_BOTS } from './constants';

const EMPTY_OPTIONS = [];

const BaseMultiSelect = ({ area, ...props }) => (
  <MultiSelect
    {...omitFormProps(props)}
    loading={area.loading}
    options={area.data || EMPTY_OPTIONS}
  />
);

const PlatformsField = withFormField({
  normalizeValue: normalizeMultiSelectValue,
})((props) => {
  const setMultipleValues = useChangeMultipleValues(
    (fieldValue, formValues) => {
      if (fieldValue?.length) {
        return {
          ...formValues,
          [props.name]: fieldValue,
        };
      }
      return {
        ...formValues,
        [DEVICES]: undefined,
        [OSVERSIONS]: undefined,
        [props.name]: fieldValue,
      };
    }
  );
  return <BaseMultiSelect {...props} onChange={setMultipleValues} />;
});

const NetworksField = withFormField({
  normalizeValue: normalizeMultiSelectValue,
})((props) => {
  const setMultipleValues = useChangeMultipleValues(
    (fieldValue, formValues) => {
      const mobile = hasMobileNetworks(fieldValue);

      if (mobile) {
        return {
          ...formValues,
          [props.name]: fieldValue,
        };
      }

      return {
        ...formValues,
        [DEVICES]: undefined,
        [OSVERSIONS]: undefined,
        [props.name]: fieldValue,
      };
    }
  );

  return <BaseMultiSelect {...props} onChange={setMultipleValues} />;
});

const FieldSet = () => {
  const { values, setFieldValue } = useContext(FormContext);

  const canEditAll = useCanEditAll();

  let networks = getIn(values, NETWORKS);
  let platforms = getIn(values, PLATFORMS);
  let trafficType = getIn(values, TRAFFIC_TYPE);
  let applications = getIn(values, APPLICATIONS);
  let onlyApplications = getIn(values, ONLY_APPLICATIONS);
  let excludeApplications = getIn(values, EXCLUDE_APPLICATIONS);

  const isShowAffiliates = useMemo(() => {
    return [TRAFFIC_TYPE_ADVERTISING, TRAFFIC_TYPE_ALL].includes(trafficType);
  }, [trafficType]);

  //reset "exclude applications" if select "only applications"
  useEffect(() => {
    if (onlyApplications && applications && excludeApplications) {
      setFieldValue(EXCLUDE_APPLICATIONS, undefined);
    }
  }, [onlyApplications]);

  const mobile = hasMobileNetworks(networks);
  const hasPlatforms = platforms?.length > 0;

  //reset devices/os if networks not mobile
  // useEffect(() => {
  //   if (!mobile) {
  //     setFieldValue(DEVICES, undefined);
  //     setFieldValue(OSVERSIONS, undefined);
  //   }
  // }, [networks]);

  return (
    <Section title={'Population'}>
      <Grid container={true} spacing={3}>
        <Grid item xs={4}>
          <FormField.Select
            disabled={!canEditAll}
            label={'Traffic type'}
            name={TRAFFIC_TYPE}
            source={loadTrafficTypes}
            padding={0}
            lazy={true}
            required={true}
          />
        </Grid>
        <Grid item xs={4}>
          <NetworksField
            disabled={!canEditAll}
            label={'Network'}
            name={NETWORKS}
            source={loadNetworks}
            padding={0}
            required={true}
            lazy={true}
          />
        </Grid>
        <Grid item xs={4}>
          <PlatformsField
            disabled={!canEditAll}
            label={'Platforms'}
            name={PLATFORMS}
            depends={DEPENDENCIES[PLATFORMS]}
            source={loadPlatforms}
            padding={0}
            lazy={true}
            required={true}
          />
        </Grid>
        {mobile ? (
          <Grid item xs={8}>
            <FormField.MultiSelect
              disabled={!canEditAll || !hasPlatforms}
              label={'Devices'}
              name={DEVICES}
              depends={DEPENDENCIES[DEVICES]}
              source={loadDevices}
              padding={0}
              placeholder={'All'}
              active={!!hasPlatforms}
              lazy={true}
            />
          </Grid>
        ) : null}
        {mobile ? (
          <Grid item xs={4}>
            <FormField.MultiSelect
              disabled={!canEditAll || !hasPlatforms}
              label={'OS'}
              name={OSVERSIONS}
              depends={DEPENDENCIES[OSVERSIONS]}
              source={loadOSversions}
              padding={0}
              placeholder={'All'}
              lazy={true}
            />
          </Grid>
        ) : null}

        <Grid item xs={6}>
          <FormField.MultiSelect
            disabled={!canEditAll}
            label={'Applications'}
            name={APPLICATIONS}
            depends={DEPENDENCIES[APPLICATIONS]}
            source={loadApplications}
            padding={0}
            required={true}
            lazy={true}
          />
        </Grid>

        <Grid item xs={6}>
          <FormField.Checkbox
            disabled={!canEditAll}
            label={"Never registered in the company's other applications"}
            padding={0}
            name={ONLY_APPLICATIONS}
          />
        </Grid>
        <Grid item xs={6}>
          <FormField.MultiSelect
            disabled={!canEditAll}
            label={'Exclude applications'}
            name={EXCLUDE_APPLICATIONS}
            depends={DEPENDENCIES[EXCLUDE_APPLICATIONS]}
            source={loadExcludeApplications}
            padding={0}
            active={!onlyApplications}
            lazy={true}
          />
        </Grid>
        <Grid item xs={6}>
          <FormField.MultiSelect
            disabled={!canEditAll}
            label={'Countries'}
            name={COUNTRIES}
            source={loadCountries}
            padding={0}
            placeholder={'All'}
            lazy={true}
          />
        </Grid>

        {isShowAffiliates ? (
          <Grid item xs={6}>
            <FormField.MultiSelect
              disabled={!canEditAll}
              label={'Affiliates'}
              name={AFFILIATES}
              source={loadAffiliates}
              padding={0}
              placeholder={'All'}
              lazy={true}
              _cleanOnUnmount={true}
            />
          </Grid>
        ) : null}
        <Grid item xs={12}>
          <FormField.Checkbox
            disabled={!canEditAll}
            label={'Include bots'}
            padding={0}
            name={IS_INCLUDE_BOTS}
          />
        </Grid>
      </Grid>
    </Section>
  );
};
export default FieldSet;
